import * as Cesium from 'cesium';
import { prefersReducedMotion } from '../util/motion';

/**
 * Phase 9 chunk 1 — context-aware zoom controller.
 *
 * Pure-math `chooseZoomAction` decides which of three flight presets
 * the next `<sat-zoom-button>` click should run, based on the current
 * selection + camera-to-satellite distance. Cesium-using wrappers
 * (`flyToCloseUpNoSelection`, `flyToCloseUpTracking`,
 * `flyToContextTracking`) execute the chosen preset, honoring
 * `prefers-reduced-motion` (forces `duration: 0` for an instant cut).
 *
 * Altitudes are locked in docs/phase9.md § II row 2:
 *   - Close-up, no selection: 1,500 km above Earth surface
 *   - Close-up, tracking:       800 km above the satellite
 *   - Context, tracking:     10,000 km above the satellite
 *
 * NEAR_THRESHOLD_M (5,000 km) splits "tracking close-up" from
 * "camera elsewhere" — at-or-below the threshold means the next
 * click pulls out to context view; above means the next click
 * re-centers down on the satellite.
 */

export const CLOSEUP_NO_SEL_HEIGHT_M  = 1_500_000;
export const CLOSEUP_TRACKING_HEIGHT_M =   800_000;
export const CONTEXT_TRACKING_HEIGHT_M = 10_000_000;
export const NEAR_THRESHOLD_M          = 5_000_000;
export const FLIGHT_DURATION_S = 1.5;

export type ZoomAction = 'in-no-sel' | 'out-context' | 'recenter';

/**
 * Phase 11 follow-up — altitude-preset scale that backs the
 * `<sat-zoom-button>` no-selection dropdown. Each preset is an
 * altitude above the WGS84 ellipsoid surface. Calling out the
 * LEO / MEO / GEO bands directly mirrors how operators describe
 * orbital regimes; the Wide bookend gives the user an "all the
 * way out" endpoint that fits every active orbit in frame.
 *
 * Altitudes (above-ellipsoid):
 *   - LEO  — 1,500 km (matches CLOSEUP_NO_SEL_HEIGHT_M; the
 *            original default for "⊕ Zoom in")
 *   - MEO  — 20,200 km, the GPS / GNSS constellation belt
 *   - GEO  — 35,786 km, the geostationary belt (exact value)
 *   - Wide — 50,000 km, comfortably above GEO so every active
 *            orbit fits in the frame
 *
 * Order matters: the dropdown renders these top-to-bottom in this
 * "close → far" sequence so the gesture matches the user's mental
 * model (top of the menu = zoom in, bottom = zoom out).
 */
export interface AltitudePreset {
  /** Stable key used by `findClosestAltitudePreset` and test selectors. */
  key: 'leo' | 'meo' | 'geo' | 'wide';
  /** Short label shown in the dropdown row. */
  label: string;
  /** Altitude above the WGS84 ellipsoid surface, meters. */
  altitudeM: number;
  /** One-line context (target orbital regime + signature satellites). */
  description: string;
}

export const ALTITUDE_PRESETS: ReadonlyArray<AltitudePreset> = [
  { key: 'leo',  label: 'LEO',  altitudeM:  1_500_000, description: 'Default — ISS, Starlink (~1,500 km)' },
  { key: 'meo',  label: 'MEO',  altitudeM: 20_200_000, description: 'GPS / GNSS belt (~20,200 km)' },
  { key: 'geo',  label: 'GEO',  altitudeM: 35_786_000, description: 'Geostationary belt (~35,786 km)' },
  { key: 'wide', label: 'Wide', altitudeM: 50_000_000, description: 'All Earth orbits (~50,000 km)' },
];

/**
 * Pure-math: given the camera's current altitude above the WGS84
 * ellipsoid, return the closest preset's key (so the dropdown can
 * highlight it). Returns null for non-finite inputs so callers can
 * skip highlighting cleanly while the viewer is still initializing.
 */
export function findClosestAltitudePreset(altitudeM: number | null): AltitudePreset['key'] | null {
  if (altitudeM === null || !Number.isFinite(altitudeM)) return null;
  let bestKey: AltitudePreset['key'] = ALTITUDE_PRESETS[0].key;
  let bestDelta = Math.abs(ALTITUDE_PRESETS[0].altitudeM - altitudeM);
  for (const p of ALTITUDE_PRESETS) {
    const d = Math.abs(p.altitudeM - altitudeM);
    if (d < bestDelta) { bestDelta = d; bestKey = p.key; }
  }
  return bestKey;
}

/**
 * Pick the next action based on selection + measured distance from
 * the camera to the satellite. `satPosition` may be null when a sat
 * is selected but the worker hasn't reported its position yet — in
 * that case we fall back to `'recenter'` so a click still does
 * something reasonable once the position lands.
 *
 * Pure math; Cesium-free so the decision lives in Vitest.
 */
export function chooseZoomAction(
  selectedNorad: number | null,
  cameraToSatDistanceM: number | null,
): ZoomAction {
  if (selectedNorad === null) return 'in-no-sel';
  if (cameraToSatDistanceM === null || !Number.isFinite(cameraToSatDistanceM)) {
    return 'recenter';
  }
  return cameraToSatDistanceM <= NEAR_THRESHOLD_M ? 'out-context' : 'recenter';
}

/**
 * Distance in meters between two ECEF Cartesian3 points. Defensive
 * against NaN / Infinity inputs (returns `null`) so callers can fall
 * through to the safe `'recenter'` action.
 */
export function ecefDistanceMeters(a: Cesium.Cartesian3, b: Cesium.Cartesian3): number | null {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  const d2 = dx * dx + dy * dy + dz * dz;
  if (!Number.isFinite(d2) || d2 < 0) return null;
  return Math.sqrt(d2);
}

/**
 * Fly the camera straight down on the current ground-track point at
 * the requested altitude above the WGS84 ellipsoid surface. Heading
 * forces north-up, pitch forces nadir; reduced-motion forces an
 * instant cut.
 *
 * This is the building block for the chunk-1 no-selection action
 * ({@link flyToCloseUpNoSelection}) AND the Phase-11-follow-up
 * altitude-preset dropdown rendered when no satellite is selected.
 */
export function flyToAltitude(viewer: Cesium.Viewer, altitudeM: number): void {
  const carto = viewer.camera.positionCartographic;
  const lat = Number.isFinite(carto.latitude) ? Cesium.Math.toDegrees(carto.latitude) : 0;
  const lon = Number.isFinite(carto.longitude) ? Cesium.Math.toDegrees(carto.longitude) : 0;
  viewer.camera.flyTo({
    destination: Cesium.Cartesian3.fromDegrees(lon, lat, altitudeM),
    orientation: { heading: 0, pitch: -Math.PI / 2, roll: 0 },
    duration: prefersReducedMotion() ? 0 : FLIGHT_DURATION_S,
  });
}

/**
 * Fly the camera to a wide-Earth view at {@link CLOSEUP_NO_SEL_HEIGHT_M}
 * above the current camera ground-track point. Now a thin wrapper
 * around {@link flyToAltitude}; preserved for any caller that still
 * references the chunk-1 helper directly.
 */
export function flyToCloseUpNoSelection(viewer: Cesium.Viewer): void {
  flyToAltitude(viewer, CLOSEUP_NO_SEL_HEIGHT_M);
}

/**
 * Fly the camera straight down on `satPosition` from
 * {@link CLOSEUP_TRACKING_HEIGHT_M}. "Recenter on this satellite"
 * — pairs with the ⊙ icon when the camera has wandered far from
 * the selected sat.
 */
export function flyToCloseUpTracking(viewer: Cesium.Viewer, satPosition: Cesium.Cartesian3): void {
  const carto = Cesium.Cartographic.fromCartesian(satPosition);
  const lat = Cesium.Math.toDegrees(carto.latitude);
  const lon = Cesium.Math.toDegrees(carto.longitude);
  viewer.camera.flyTo({
    destination: Cesium.Cartesian3.fromDegrees(lon, lat, carto.height + CLOSEUP_TRACKING_HEIGHT_M),
    orientation: { heading: 0, pitch: -Math.PI / 2, roll: 0 },
    duration: prefersReducedMotion() ? 0 : FLIGHT_DURATION_S,
  });
}

/**
 * Fly the camera straight down on `satPosition` from
 * {@link CONTEXT_TRACKING_HEIGHT_M}. "Pull out so the constellation
 * is visible" — pairs with the ⊖ icon when the camera is already
 * near the satellite.
 */
export function flyToContextTracking(viewer: Cesium.Viewer, satPosition: Cesium.Cartesian3): void {
  const carto = Cesium.Cartographic.fromCartesian(satPosition);
  const lat = Cesium.Math.toDegrees(carto.latitude);
  const lon = Cesium.Math.toDegrees(carto.longitude);
  viewer.camera.flyTo({
    destination: Cesium.Cartesian3.fromDegrees(lon, lat, carto.height + CONTEXT_TRACKING_HEIGHT_M),
    orientation: { heading: 0, pitch: -Math.PI / 2, roll: 0 },
    duration: prefersReducedMotion() ? 0 : FLIGHT_DURATION_S,
  });
}
